/**
 * Pure mapper: the DEMA-WMA Gainer comparison-chart response -> Lightweight Charts series and
 * markers. No React, no network. Nothing is recalculated: bars, indicators and events are the
 * backend's (events are already placed on their 5-minute bar, `bar_time`).
 *
 * Visual language (same as the exported TradingView script):
 *   BACKTEST = blue family, PAPER = orange family;
 *   entries = arrows (CE up, below the bar / PE down, above the bar), open paper position = yellow circle;
 *   exits = squares coloured by exit reason (stop-loss red, target green, WMA21 slope purple, EOD grey,
 *           manual / kill-switch black); backtest exits above the bar, paper exits below it;
 *   rejected signals = translucent circle "REJ"; skipped crosses (WMA21 slope disagreed) = small faint circle.
 */
import type { DemaWmaChart, DemaWmaChartEvent, DemaWmaExitClass } from "@/services/demaWmaGainerService";
import { isoToEpochSeconds } from "@/components/charts/zonedTime";

export const COLORS = {
  backtest: "#1E88E5",
  paper: "#FB8C00",
  open: "#FDD835",
  backtestRejected: "rgba(30, 136, 229, 0.55)",
  paperRejected: "rgba(251, 140, 0, 0.6)",
  backtestSkipped: "rgba(144, 202, 249, 0.7)",
  paperSkipped: "rgba(255, 204, 128, 0.8)",
  demaFast: "#00897B",
  demaSlow: "#6D4C41",
  rsi: "rgba(120, 144, 156, 0.6)",
  wmaUp: "#43A047",
  wmaDown: "#E53935",
  wmaFlat: "#9E9E9E",
  baseline: "#00897B",
} as const;

export const EXIT_COLORS: Record<DemaWmaExitClass, string> = {
  STOP_LOSS: "#E53935",
  TARGET: "#43A047",
  WMA_SLOPE: "#8E24AA",
  EOD: "#757575",
  MANUAL: "#000000",
  OTHER: "#546E7A",
};

export const EXIT_SHORT: Record<DemaWmaExitClass, string> = {
  STOP_LOSS: "SL",
  TARGET: "TGT",
  WMA_SLOPE: "WMA",
  EOD: "EOD",
  MANUAL: "MANUAL",
  OTHER: "X",
};

export const EXIT_LABEL: Record<DemaWmaExitClass, string> = {
  STOP_LOSS: "Stop-loss hit",
  TARGET: "Target hit",
  WMA_SLOPE: "WMA21 slope exit",
  EOD: "15:15 square-off",
  MANUAL: "Manual / kill-switch exit",
  OTHER: "Other exit",
};

export interface Layers {
  backtest: boolean;
  paper: boolean;
  open: boolean;
  rejected: boolean;
  skipped: boolean;
  exits: Record<DemaWmaExitClass, boolean>;
}

export const DEFAULT_LAYERS: Layers = {
  backtest: true,
  paper: true,
  open: true,
  rejected: true,
  skipped: false,
  exits: { STOP_LOSS: true, TARGET: true, WMA_SLOPE: true, EOD: true, MANUAL: true, OTHER: true },
};

export interface Marker {
  time: number;
  position: "aboveBar" | "belowBar" | "inBar";
  shape: "arrowUp" | "arrowDown" | "circle" | "square";
  color: string;
  text?: string;
  id?: string;
  size?: number;
}

export interface Point { time: number; value: number; color?: string }

export interface ChartData {
  times: number[];
  candles: { time: number; open: number; high: number; low: number; close: number }[];
  demaFast: Point[];
  demaSlow: Point[];
  rsi: Point[];
  wma: Point[];
  baseline: Point[];
  backtestPoints: Point[];
  paperPoints: Point[];
  /** Events per bar index (for the hover panel). */
  eventsByBar: Map<number, DemaWmaChartEvent[]>;
}

function line(times: number[], values: (number | null)[] | null | undefined): Point[] {
  const out: Point[] = [];
  if (!values) return out;
  values.forEach((v, i) => {
    if (v !== null && v !== undefined && Number.isFinite(v) && times[i] !== undefined) out.push({ time: times[i], value: v });
  });
  return out;
}

export function buildChartData(chart: DemaWmaChart): ChartData {
  const times: number[] = [];
  const candles: ChartData["candles"] = [];
  chart.bars.forEach((b) => {
    const t = isoToEpochSeconds(b.t) ?? 0;
    times.push(t);
    if (b.valid && [b.o, b.h, b.l, b.c].every((v) => Number.isFinite(v) && v > 0)) {
      candles.push({ time: t, open: b.o, high: b.h, low: b.l, close: b.c });
    }
  });
  const wma: Point[] = [];
  chart.indicators.wma.forEach((v, i) => {
    if (v === null || v === undefined) return;
    const prev = i > 0 ? chart.indicators.wma[i - 1] : null;
    const color = prev === null || prev === undefined ? COLORS.wmaFlat : v > prev ? COLORS.wmaUp : v < prev ? COLORS.wmaDown : COLORS.wmaFlat;
    wma.push({ time: times[i], value: v, color });
  });
  const eventsByBar = new Map<number, DemaWmaChartEvent[]>();
  for (const e of chart.events) {
    const list = eventsByBar.get(e.bar_index) ?? [];
    list.push(e);
    eventsByBar.set(e.bar_index, list);
  }
  return {
    times,
    candles,
    demaFast: line(times, chart.indicators.dema_fast),
    demaSlow: line(times, chart.indicators.dema_slow),
    rsi: line(times, chart.indicators.rsi),
    wma,
    baseline: line(times, chart.performance.dema_baseline_points),
    backtestPoints: line(times, chart.performance.backtest_points),
    paperPoints: line(times, chart.performance.paper_points),
    eventsByBar,
  };
}

export function eventVisible(e: DemaWmaChartEvent, layers: Layers): boolean {
  if (e.source === "BACKTEST" && !layers.backtest) return false;
  if (e.source === "PAPER" && !layers.paper) return false;
  if (e.kind === "OPEN") return layers.open;
  if (e.kind === "REJECTED") return layers.rejected;
  if (e.kind === "SKIPPED") return layers.skipped;
  if (e.kind === "EXIT") return layers.exits[e.exit_class ?? "OTHER"];
  return true;
}

export function eventMarker(e: DemaWmaChartEvent, time: number): Marker {
  const bt = e.source === "BACKTEST";
  const tag = bt ? "BT" : "P";
  const up = e.side !== "PE";
  switch (e.kind) {
    case "ENTRY":
      return { time, id: e.id, position: up ? "belowBar" : "aboveBar", shape: up ? "arrowUp" : "arrowDown",
        color: bt ? COLORS.backtest : COLORS.paper, text: `${tag} ${e.side ?? ""}`.trim() };
    case "OPEN":
      return { time, id: e.id, position: up ? "belowBar" : "aboveBar", shape: "circle", color: COLORS.open,
        text: `P OPEN ${e.side ?? ""}`.trim(), size: 1.5 };
    case "EXIT": {
      const cls = e.exit_class ?? "OTHER";
      return { time, id: e.id, position: bt ? "aboveBar" : "belowBar", shape: "square", color: EXIT_COLORS[cls],
        text: `${tag} ${EXIT_SHORT[cls]}` };
    }
    case "REJECTED":
      return { time, id: e.id, position: "aboveBar", shape: "circle", color: bt ? COLORS.backtestRejected : COLORS.paperRejected,
        text: `${tag} REJ` };
    default:
      return { time, id: e.id, position: "aboveBar", shape: "circle", color: bt ? COLORS.backtestSkipped : COLORS.paperSkipped,
        size: 0.6 };
  }
}

export function buildMarkers(chart: DemaWmaChart, data: ChartData, layers: Layers): Marker[] {
  const out: Marker[] = [];
  for (const e of chart.events) {
    const t = data.times[e.bar_index];
    if (t === undefined || !eventVisible(e, layers)) continue;
    out.push(eventMarker(e, t));
  }
  // Lightweight Charts needs markers in time order; ties keep the backend's order (stable sort).
  return out.sort((a, b) => a.time - b.time);
}

export function describeEvent(e: DemaWmaChartEvent): string {
  const src = e.source === "BACKTEST" ? "Backtest" : "Paper";
  const t = (e.time ?? e.bar_time).slice(11, 16);
  const side = e.side ?? "";
  const money = (v: number | null | undefined) => (v === null || v === undefined ? "" : ` · net ₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);
  switch (e.kind) {
    case "ENTRY":
    case "OPEN":
      return `${src} ${e.kind === "OPEN" ? "OPEN position" : "entry"} ${side} ${t}${e.strike ? ` · ${e.strike} strike` : ""}${
        e.premium != null ? ` @ ${e.premium}` : ""}${e.kind === "OPEN" && e.unrealized_pnl != null ? ` · unrealised ₹${e.unrealized_pnl}` : ""}`;
    case "EXIT":
      return `${src} exit ${side} ${t} · ${EXIT_LABEL[e.exit_class ?? "OTHER"]} (${e.reason ?? "?"})${e.premium != null ? ` @ ${e.premium}` : ""}${money(e.net_pnl)}`;
    case "REJECTED":
      return `${src} signal ${side} rejected ${t} · ${e.reason ?? ""}`;
    default:
      return `${src} DEMA cross ${side} skipped ${t} · WMA21 slope disagreed`;
  }
}

export const COMPARE_STATUS_LABEL = {
  MATCHED: "Matched",
  EXIT_DIFFERS: "Exit differs",
  BACKTEST_ONLY: "Backtest only",
  PAPER_ONLY: "Paper only",
  PAPER_OPEN: "Paper open",
} as const;
